import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Plus, Trash2, Mail } from "lucide-react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useFilters } from "@/context/FilterContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { BlockSkeleton } from "@/components/Loading";
import { BrandSelectField } from "@/components/BrandSelectField";
import { ExportButton } from "@/components/ExportButton";
import { ChartCard, ChartTooltip, CHART_COLORS } from "@/components/charts";
import { fmtDateTime, fmtNumber } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const axisStyle = { fontSize: 12, fill: "rgba(48,56,60,0.62)" };
const shortDate = (d) => { const dt = new Date(d); return isNaN(dt) ? d : dt.toLocaleDateString("en-US", { month: "short", day: "numeric" }); };

export default function Signups() {
  const { queryParams } = useFilters();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ brand_id: "", email: "", name: "", source: "website" });
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/signups", { params: queryParams })
      .then((res) => setItems(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [queryParams]);

  useEffect(() => { load(); }, [load]);

  const growth = useMemo(() => {
    const byDay = {};
    items.forEach((s) => { const d = (s.created_at || "").slice(0, 10); if (d) byDay[d] = (byDay[d] || 0) + 1; });
    return Object.keys(byDay).sort().map((d) => ({ date: d, signups: byDay[d] }));
  }, [items]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/signups", form);
      toast.success("Signup added");
      setDialogOpen(false);
      setForm({ brand_id: "", email: "", name: "", source: "website" });
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/signups/${deleteId}`); toast.success("Signup removed"); setDeleteId(null); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="space-y-6">
      <PageHeader title="Newsletter Signups" subtitle="Email list growth across the selected period.">
        <ExportButton resource="signups" params={queryParams} />
        <Button data-testid="add-signup-button" onClick={() => setDialogOpen(true)} className="h-9 gap-2 rounded-full bg-[#18344A] text-white hover:bg-[#18344A]/90">
          <Plus className="h-4 w-4" /> Add signup
        </Button>
      </PageHeader>

      <ChartCard title={`Signups growth \u00b7 ${fmtNumber(items.length)} total`} testId="chart-signups-growth">
        {growth.length === 0 ? (
          <EmptyState title="No signups in range" icon={Mail} />
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={growth} margin={{ left: -14, right: 8, top: 8 }}>
              <defs>
                <linearGradient id="gSignup" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={CHART_COLORS.signups} stopOpacity={0.3} />
                  <stop offset="100%" stopColor={CHART_COLORS.signups} stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(82,110,126,0.18)" vertical={false} />
              <XAxis dataKey="date" tickFormatter={shortDate} tick={axisStyle} axisLine={false} tickLine={false} minTickGap={28} />
              <YAxis tick={axisStyle} axisLine={false} tickLine={false} width={36} />
              <Tooltip content={<ChartTooltip valueFormatter={(v) => fmtNumber(v)} />} labelFormatter={shortDate} />
              <Area type="monotone" dataKey="signups" name="Signups" stroke={CHART_COLORS.signups} strokeWidth={2} fill="url(#gSignup)" />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <Card className="overflow-hidden rounded-[14px] border-[var(--pb-border)] bg-white shadow-[var(--pb-shadow-sm)]">
        {loading ? (
          <div className="p-4"><BlockSkeleton className="h-80" /></div>
        ) : items.length === 0 ? (
          <EmptyState title="No signups found" message="Add a subscriber to get started." icon={Mail}
            action={<Button onClick={() => setDialogOpen(true)} className="rounded-full bg-[#18344A] text-white hover:bg-[#18344A]/90"><Plus className="mr-2 h-4 w-4" />Add signup</Button>} />
        ) : (
          <div className="overflow-x-auto">
            <Table data-testid="signups-table">
              <TableHeader>
                <TableRow className="bg-[rgba(24,52,74,0.04)]">
                  <TableHead>Email</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Source</TableHead>
                  <TableHead>Signed up</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((s) => (
                  <TableRow key={s.id} className="hover:bg-[rgba(183,165,142,0.1)]">
                    <TableCell className="font-medium text-[#30383C]">{s.email}</TableCell>
                    <TableCell className="text-sm">{s.name || "\u2014"}</TableCell>
                    <TableCell className="text-sm capitalize text-[rgba(48,56,60,0.7)]">{s.source}</TableCell>
                    <TableCell className="text-sm">{fmtDateTime(s.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" data-testid={`delete-signup-${s.id}`} onClick={() => setDeleteId(s.id)} className="h-8 w-8 text-[#9a3a3a]"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-display text-[#18344A]">Add signup</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
            <BrandSelectField value={form.brand_id} onChange={set("brand_id")} />
            <div className="space-y-1.5"><Label>Email</Label><Input type="email" required data-testid="signup-email" value={form.email} onChange={(e) => set("email")(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name")(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Source</Label><Input value={form.source} onChange={(e) => set("source")(e.target.value)} placeholder="website" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="signup-form-submit" className="bg-[#18344A] text-white hover:bg-[#18344A]/90">{saving ? "Saving\u2026" : "Add"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove signup?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-signup" onClick={confirmDelete} className="bg-[#9a3a3a] text-white hover:bg-[#9a3a3a]/90">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
